import exportFromJSON from "export-from-json";
import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useParams } from "react-router";
import i18next from "src/i18n/config";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { Tooltip } from "src/ui/shared/Tooltip";
import { isValidZipUrl } from "src/ui/utils/validityChecker";

import * as Fragments from "@dashboard/provisioning/components";
import { useStyles } from "@dashboard/provisioning/ProvisioningPage.style";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning } from "@dashboard/provisioning/types";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import CompareService from "@services/compareService/compareService";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { CollapsibleGroupProvider } from "../shared/components/CollapsibleSection";

import ExpandAllButton from "./components/ExpandAllButton";
import { getDefaultProvisioning } from "./utils/getDefaultProvisioning";
import { provisioningSettingsConverter } from "./utils/provisioningSettingsConverter";
import { getAllSectionExpandedState, useUniversalLicenses } from "./ProvisioningPage.utils";
import { updateProvisioning } from "./store";
import { defaultProvisioning } from "./utils";

export type FormValues = Provisioning & {
    registrationGroupId: number | undefined;
};

const ProvisioningPage = () => {
    const { provisioningId: preselectedProvisioningId } = useParams<{ provisioningId: string }>();
    const getUniversalLicenses = useUniversalLicenses();

    const { t } = useTranslation(["pages", "common"]);
    const { classes } = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch();
    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const provisionings = useSelector(selectProvisionings, shallowEqual);

    const [selectedProvisioningId, setSelectedProvisioningId] = useState<number | null>(() => {
        const preselected = provisionings.find((p) => p.id.toString() === preselectedProvisioningId);
        return (preselected || getDefaultProvisioning(provisionings))?.id ?? null;
    });

    const selectedProvisioning =
        provisionings.find((p) => p.id === selectedProvisioningId) || getDefaultProvisioning(provisionings);

    useEffect(() => {
        if (selectedProvisioning?.id) {
            UserSettingsService.setWorkspaceValue("provisioningPage.lastSelectedProvisioning", selectedProvisioning.id);
        }
    }, [selectedProvisioning?.id]);

    const onValidate = (values: FormValues) => {
        if (values.settings?.bootAnimationUrl && !isValidZipUrl(values.settings.bootAnimationUrl)) {
            return {
                settings: { bootAnimationUrl: t("pages:provisioningPage.input.bootAnimationUrl.validation.invalidZipUrl") },
            };
        }
    };
    const onSubmit = async ({ registrationGroupId, ...values }: FormValues) => {
        const universalLicenses = await getUniversalLicenses(registrationGroupId, values.settings?.universalLicenses);

        if (workspaceId && values.settings) {
            await dispatch(
                updateProvisioning({
                    ...values,
                    workspaceId,
                    settings: { ...values.settings, universalLicenses: universalLicenses || [] },
                }),
            )
                .unwrap()
                .then(() => {
                    showSuccessToast(t("pages:provisioningPage.toast.provisioningSavedSuccessfully"));
                })
                .catch(({ message = t("pages:provisioningPage.toast.provisioningUpdateFailed") }) => showErrorToast(message));
        }
    };
    const onExportProvisioningSettings = async (values: FormValues | undefined) => {
        if (!values) {
            return;
        }

        if (values.settings) {
            const data = await provisioningSettingsConverter(values.settings);
            if (data) {
                exportFromJSON({
                    data,
                    fileName: `provisioning_${values.name}`,
                    exportType: exportFromJSON.types.json,
                });
            }
        }
    };
    const getExportButtonTooltip = (formHasChanged: boolean) => {
        if (!provisionings.length) {
            return t("pages:provisioningPage.tooltip.noProvisioning");
        }
        if (formHasChanged) {
            return t("pages:provisioningPage.tooltip.saveOrDiscardChanges");
        }
        return undefined;
    };

    return (
        <CollapsibleGroupProvider initialValue={getAllSectionExpandedState()}>
            <Form<FormValues>
                onSubmit={onSubmit}
                validate={onValidate}
                initialValues={selectedProvisioning || defaultProvisioning}
                render={({ handleSubmit, submitting, values, form, initialValues }) => {
                    const formHasChanged = new CompareService(initialValues, values).hasDifferences;
                    return (
                        <form onSubmit={handleSubmit} className={classes.form}>
                            <PageContainer>
                                <ExpiredWrapper>
                                    <CustomAlert severity="info" style={{ marginBottom: theme.spacing(6) }}>
                                        {t("pages:provisioningPage.alert.notAffectInstalledDevices")}
                                    </CustomAlert>

                                    <Fragments.ProvisioningManagement onSelect={setSelectedProvisioningId} />
                                    <Fragments.SetupAutomation />
                                    <Fragments.InternetConfig />
                                    <Fragments.DeviceRegistration />
                                    <Fragments.Screen />
                                    <Fragments.ProductBranding />
                                    <Fragments.ProductAdministration />
                                    <Fragments.DisabledComponents />
                                    <Fragments.HostedApks />
                                    <Fragments.RuntimePermissions />
                                    <Fragments.PrivateRepositories />
                                    <Fragments.ApplicationPreferences />
                                    <Fragments.SshKeys />
                                    <Fragments.Connectivity />
                                    <Fragments.SystemSettings />
                                    <Fragments.SystemProperties />
                                </ExpiredWrapper>
                            </PageContainer>
                            <Box className={classes.stickyFooter}>
                                <ExpandAllButton />
                                <Tooltip title={getExportButtonTooltip(!!formHasChanged)}>
                                    <SecondaryButton
                                        name="export"
                                        onClick={() => onExportProvisioningSettings(values)}
                                        disabled={submitting || !workspaceId || !provisionings.length || !!formHasChanged}
                                        startIcon={<i className="fa-solid fa-file-export" />}
                                    >
                                        {t("common:button.export")}
                                    </SecondaryButton>
                                </Tooltip>
                                <Box className={classes.footerRightContainer}>
                                    <SecondaryButton
                                        name="cancel"
                                        onClick={() => form.restart(initialValues)}
                                        disabled={!formHasChanged || submitting || !workspaceId || !provisionings.length}
                                    >
                                        {t("common:button.cancel")}
                                    </SecondaryButton>
                                    <PrimaryButton
                                        name="save"
                                        type="submit"
                                        disabled={submitting || !provisionings.length || !formHasChanged}
                                        loading={submitting}
                                    >
                                        {t("common:button.save")}
                                    </PrimaryButton>
                                </Box>
                            </Box>
                        </form>
                    );
                }}
            />
        </CollapsibleGroupProvider>
    );
};

export default withMetaDescription(ProvisioningPage, {
    title: i18next.t("pages:provisioningPage.metaTitle"),
    description: i18next.t("pages:provisioningPage.metaDescription"),
});
